import * as React from 'react';
import { Image } from 'antd';
import NotesMediaPhotoTile from './NotesMediaPhotoTile';
import NotesMediaVideoTile from './NotesMediaVideoTile';
import { NOTE_MEDIA_TYPE_VIDEO, NoteMediaItem } from '../../utils/NoteMediaUtils';

// Tiles are square whatever their media's ratio. The height comes from a pseudo-element's
// `padding-bottom: 100%` rather than `aspect-ratio` or the aspect-ratio plugin, because percentage
// padding resolves against the CONTAINING BLOCK: on the tile itself that is the flex row (making
// every tile as tall as the panel is wide), while on a child it is the tile. The tile's own media
// is then positioned absolutely on top of it.
const TILE_SHELL_CLASS_NAME =
	"relative overflow-hidden rounded before:block before:pb-[100%] before:content-['']";

// The All view keeps every tile on one row that runs past the panel's edge; the Media filter wraps
// them into columns that fill the panel edge to edge at any width.
const CONTAINER_CLASS_NAME_BY_VARIANT = {
	strip: 'no-scrollbar mb-5 flex flex-row gap-1.5 overflow-x-auto',
	grid: 'grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-1.5',
};

const TILE_CLASS_NAME_BY_VARIANT = {
	strip: `${TILE_SHELL_CLASS_NAME} w-[104px] shrink-0`,
	grid: TILE_SHELL_CLASS_NAME,
};

interface NotesMediaTilesProps {
	/** Photos and videos interleaved, in the order they were shared. */
	items: NoteMediaItem[];
	variant: 'grid' | 'strip';
	onOpenVideo: (item: NoteMediaItem) => void;
	/** The scrolling container itself, so a caller can tell whether the tiles overflow it. */
	containerRef?: React.Ref<HTMLDivElement>;
}

/**
 * The photo/video tiles. Every photo shares one preview group, so the lightbox pages through the
 * whole conversation's photos; videos are not part of that group and open in their own player.
 */
const NotesMediaTiles: React.FC<NotesMediaTilesProps> = ({
	items,
	variant,
	onOpenVideo,
	containerRef,
}) => {
	const tileClassName = TILE_CLASS_NAME_BY_VARIANT[variant];

	const renderTile = (item: NoteMediaItem) =>
		item.mediaType === NOTE_MEDIA_TYPE_VIDEO ? (
			<NotesMediaVideoTile
				key={item.key}
				item={item}
				className={tileClassName}
				onOpen={onOpenVideo}
			/>
		) : (
			<NotesMediaPhotoTile key={item.key} item={item} className={tileClassName} />
		);

	return (
		<Image.PreviewGroup>
			<div ref={containerRef} className={CONTAINER_CLASS_NAME_BY_VARIANT[variant]}>
				{items.map(renderTile)}
			</div>
		</Image.PreviewGroup>
	);
};

export default NotesMediaTiles;
